import React from "react";
import {useSelector} from "react-redux";
import GroupFilters from "./GroupFilters";
import OffersFilter from "./OffersFilter";
import TableFilterPresets from "./TableFilterPresets";
import {
    MANAGER_ANALYSIS,
    MANAGER_LTV_ANALYSIS,
    MANAGER_MAIN,
    MANAGER_MONITOR,
    MANAGER_REFERRER,
    PARTNER_MAIN,
} from "../../../../utils/constants";
import DateRangeFilter from "./DateRangeFilter";
import PeriodButtonsFilter from "./PeriodButtonsFilter";
import OperatorsFilter from "./OperatorsFilter";
import AdminPartnersFilter from "../../../common/filters/AdminPartnersFilter";
import ServiceGroupsFilter from "./ServiceGroupsFilter";
import CountriesFilter from "./CountriesFilter";
import LtvBuyoutRateFilter from "./LtvBuyoutRateFilter";
import LtvFinanceFeeFilter from "./LtvFinanceFeeFilter";
import LtvServiceTypes from "./LtvServiceTypes";
import PayoutTypes from "./PayoutTypes";
import CircleButtonsGroup from "../../../common/buttons/CircleButtonsGroup";

export default function StatisticFiltersCard(props) {
    const {
        pageState,
        selectFromDate,
        selectToDate,
        selectPeriod,
        handleChangeFilterData,
        selectGroup,
        toggleGroupsData,
        inputStyle,
        selectPreset,
        deletePreset,
        saveFiltersPreset,
        clearFilters,
        createStatisticReport,
        toggleChartBtn,
        scrollContainerRef,
    } = props;

    const auth = useSelector((state) => state.auth);
    const loading = useSelector((state) => state.statisticReducer.loading);

    const filtersPresetsScrollToBottom = () => {
        scrollContainerRef?.current?.scrollTo(0, 130);
    };

    const getTextfieldStyle = () => {
        const inputMinWidth = inputStyle?.root?.minWidth ? inputStyle.root.minWidth + "px" : "165px";
        return {display: "flex", paddingTop: "15px", minWidth: inputMinWidth};
    };

    const getDateRangeFilter = (className) => {
        return (
            <DateRangeFilter
                className={className}
                fromDate={pageState.filters.fromDate}
                toDate={pageState.filters.toDate}
                selectFromDate={selectFromDate}
                selectToDate={selectToDate}
                inputStyle={inputStyle?.root}
            />
        );
    };

    const getPeriodButtonsFilter = (className) => {
        return (
            <PeriodButtonsFilter
                className={className}
                selectedPeriod={pageState.filters.period}
                setPeriod={selectPeriod}
                statisticType={pageState.selectedTab}
            />
        );
    };

    const getLtvBuyoutRateFilter = () => {
        if (pageState.selectedTab !== MANAGER_LTV_ANALYSIS) {
            return null;
        }

        return (
            <LtvBuyoutRateFilter
                pageState={pageState}
                handleChangeFilterData={handleChangeFilterData}
                textFieldStyle={getTextfieldStyle()}
            />
        );
    };

    const getGroupsFilter = () => {
        if (pageState.selectedTab === MANAGER_REFERRER) {
            return null;
        }

        return (
            <GroupFilters
                selectedGroups={pageState.filters.groups}
                selectGroup={selectGroup}
                statisticType={pageState.selectedTab}
                inputStyle={inputStyle}
                toggleGroupsData={toggleGroupsData}
            />
        );
    };

    const getCountriesFilter = () => {
        if (pageState.selectedTab === MANAGER_REFERRER || pageState.selectedTab === MANAGER_LTV_ANALYSIS) {
            return null;
        }

        return (
            <CountriesFilter
                selectedCountries={pageState.filters.countries}
                handleChangeFilterData={handleChangeFilterData}
                inputStyle={inputStyle}
            />
        );
    };

    const getOperatorsFilter = () => {
        if (pageState.selectedTab === MANAGER_REFERRER) {
            return null;
        }

        return (
            <OperatorsFilter
                selectedOperators={pageState.filters.operators}
                handleChangeFilterData={handleChangeFilterData}
                selectedCountries={pageState.filters.countries}
                inputStyle={inputStyle}
            />
        );
    };

    const getOffersFilter = () => {
        if (pageState.selectedTab === MANAGER_REFERRER) {
            return null;
        }

        return (
            <div className={pageState.selectedTab === MANAGER_MONITOR ? "last-filter" : undefined}>
                <OffersFilter
                    selectedOffers={pageState.filters.offers}
                    selectedOperators={pageState.filters.operators}
                    selectedCountries={pageState.filters.countries}
                    handleChangeFilterData={handleChangeFilterData}
                    inputStyle={inputStyle}
                />
            </div>
        );
    };

    const getPartnersFilter = () => {
        const isLoggedAsPartner = auth.isPartner || auth.isAdminAsPartner;
        const isVisible = [MANAGER_MAIN, MANAGER_ANALYSIS, MANAGER_LTV_ANALYSIS, MANAGER_MONITOR].includes(pageState.selectedTab)
            && !isLoggedAsPartner
            && !auth.isArbitr;

        return isVisible && (
            <AdminPartnersFilter
                selectedPartners={pageState.filters.partners}
                handleChangeFilterData={(value) => handleChangeFilterData("partners", value)}
                inputStyle={inputStyle}
            />
        );
    };

    const getExcludedPartnersFilter = () => {
        const isLoggedAsPartner = auth.isPartner || auth.isAdminAsPartner;
        const isVisible = [MANAGER_MAIN, MANAGER_ANALYSIS, MANAGER_LTV_ANALYSIS, MANAGER_MONITOR].includes(pageState.selectedTab)
            && !isLoggedAsPartner
            && !auth.isArbitr;

        return isVisible && (
            <AdminPartnersFilter
                selectedPartners={pageState.filters.excludedPartners}
                label="Исключить партнеров"
                handleChangeFilterData={(value) => handleChangeFilterData("excludedPartners", value)}
                inputStyle={inputStyle}
            />
        );
    };

    const getFiltersPresetsFilter = () => {
        const isVisible = pageState.selectedTab === PARTNER_MAIN
            || pageState.selectedTab === MANAGER_MAIN
            || pageState.selectedTab === MANAGER_LTV_ANALYSIS;

        return isVisible && (
            <div onClick={filtersPresetsScrollToBottom}>
                <TableFilterPresets
                    pageState={pageState}
                    selectPreset={selectPreset}
                    saveFiltersPreset={saveFiltersPreset}
                    deletePreset={deletePreset}
                    clearAllFilters={clearFilters}
                    inputStyle={{...inputStyle, paper: {position: "relative"}}}
                />
            </div>
        );
    };

    const getServiceGroupsFilter = () => {
        const isLoggedAsPartner = auth.isPartner || auth.isAdminAsPartner;
        const isVisible = [MANAGER_MAIN, MANAGER_MONITOR].includes(pageState.selectedTab) && !isLoggedAsPartner;

        return isVisible && (
            <ServiceGroupsFilter
                selectedServiceGroups={pageState.filters.serviceGroups}
                handleChangeFilterData={handleChangeFilterData}
                inputStyle={inputStyle}
            />
        );
    };

    const getFinanceFeeFilter = () => {
        return (
            <LtvFinanceFeeFilter
                pageState={pageState}
                handleChangeFilterData={handleChangeFilterData}
                textFieldStyle={getTextfieldStyle()}
            />
        );
    };

    const getLtvServiceTypesFilter = () => {
        return (
            <LtvServiceTypes
                selectedServiceType={pageState.filters.ltvServiceType}
                handleChangeFilterData={handleChangeFilterData}
                inputStyle={inputStyle}
            />
        );
    };

    const getLtvPayoutTypes = () => {
        return (
            <PayoutTypes
                selectedPayoutType={pageState.filters.payoutType}
                handleChangeFilterData={handleChangeFilterData}
                inputStyle={inputStyle}
            />
        );
    };

    const getFilterButtons = () => {
        return (
            <CircleButtonsGroup style={{zIndex: 10000}}
                buttons={[
                    {
                        isSelected: false,
                        title: "Сформировать отчет",
                        iconClassName: "ti ti-check stat-icon",
                        onClick: createStatisticReport,
                        isLoading: loading,
                    },
                    {
                        isSelected: pageState.filters.chartBtnSelected,
                        title: "Построить график",
                        iconClassName: "ti ti-stats-up stat-icon",
                        onClick: toggleChartBtn,
                        isHidden: [MANAGER_REFERRER, MANAGER_LTV_ANALYSIS, MANAGER_MONITOR].includes(pageState.selectedTab),
                    },
                    {
                        isSelected: false,
                        title: "Очистить",
                        iconClassName: "ti ti-close stat-icon",
                        onClick: () => clearFilters(true),
                    },
                ]}
            />
        );
    };

    const getFilters = () => {
        switch (pageState.selectedTab) {
            case MANAGER_LTV_ANALYSIS:
                return (
                    <>
                        {getDateRangeFilter()}
                        {getPeriodButtonsFilter("nowrap-custom")}
                        {getGroupsFilter("")}
                        {getLtvPayoutTypes()}
                        {getPartnersFilter("")}
                        {getExcludedPartnersFilter("")}
                        {getOffersFilter("")}
                        {getOperatorsFilter("")}
                        {getLtvBuyoutRateFilter()}
                        {getFinanceFeeFilter()}
                        {getFiltersPresetsFilter("")}
                        {getFilterButtons()}
                    </>
                );
            default:
                return (
                    <>
                        {getDateRangeFilter("")}
                        {getPeriodButtonsFilter("nowrap-custom")}
                        {getGroupsFilter("")}
                        {getServiceGroupsFilter("")}
                        {getPartnersFilter("")}
                        {getExcludedPartnersFilter("")}
                        {getCountriesFilter("")}
                        {getOperatorsFilter("")}
                        {getOffersFilter("")}
                        {getFiltersPresetsFilter("")}
                        {getFilterButtons()}
                    </>
                );
        }
    };

    return <>{getFilters()}</>;
}
